import logger from '@/utils/client-logger';

const DB_NAME = 'zeelool-store';
const STORE_NAME = 'kv';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };

        resolve(db);
      };

      request.onerror = () => {
        logger.error('IndexedDB open error:', request.error);
        dbPromise = null;
        reject(request.error);
      };
    } catch (error) {
      logger.error('IndexedDB open exception:', error);
      dbPromise = null;
      reject(error);
    }
  });

  return dbPromise;
}

export async function idbSet(key: string, value: any): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        logger.error('idbSet error:', tx.error);
        reject(tx.error);
      };
    });
  } catch (error) {
    logger.error('idbSet failed for key:', key, error);
    throw error;
  }
}

export async function idbGet<T = any>(key: string): Promise<T | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(key);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => {
        logger.error('idbGet error:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    logger.error('idbGet failed for key:', key, error);
    throw error;
  }
}

export async function idbRemove(key: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        logger.error('idbRemove error:', tx.error);
        reject(tx.error);
      };
    });
  } catch (error) {
    logger.error('idbRemove failed for key:', key, error);
    throw error;
  }
}
